import { useProductUnderstandingCoverageApi } from '@/common/api/modules/productUnderstanding'
import { useSiteId } from '@/common/app/site'

import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'

const Bar = ({ pct, color }: { pct: number; color: string }) => (
  <div className='bg-surface-secondary h-2.5 w-full overflow-hidden rounded-full'>
    <div className='h-full rounded-full' style={{ width: `${Math.min(100, pct)}%`, background: color }} />
  </div>
)

const StatCard = ({ label, value, color }: { label: string; value: string; color?: string }) => (
  <div className='border-primary flex-1 rounded-xl border px-4 py-3'>
    <div className='text-tertiary text-[11px]'>{label}</div>
    <div className='mt-1 text-[22px] font-bold tabular-nums' style={color ? { color } : undefined}>
      {value}
    </div>
  </div>
)

const ProductUnderstandingCoverageSection = () => {
  const siteId = useSiteId()
  const { data, isLoading } = useProductUnderstandingCoverageApi({ site_id: siteId })

  return (
    <>
      <Text size={12} color={TextColor.SECONDARY}>
        商品理解の状態別件数、軸別の抽出充足率、素材・用途シーンの分布、処理コストを表示します。
      </Text>

      {isLoading || !data ? (
        <Area>
          <PageListSearching />
        </Area>
      ) : (
        <div className='mt-3 flex flex-col gap-4'>
          {/* 状態別件数 */}
          <div className='flex flex-wrap gap-3'>
            <StatCard label='対象商品数' value={data.total.toLocaleString()} />
            <StatCard label='理解済み（success）' value={data.success.toLocaleString()} color='#1A9E6B' />
            <StatCard label='簡易テキスト（skip含む）' value={data.fallback.toLocaleString()} color='#D08A1A' />
            <StatCard label='うち薄い商品skip' value={data.skipped.toLocaleString()} />
            <StatCard label='失敗' value={data.failed.toLocaleString()} color={data.failed ? '#D24B4B' : undefined} />
          </div>

          {/* 軸別 抽出充足率 */}
          <Area>
            <Heading>軸別 抽出充足率（理解済み {data.success.toLocaleString()}件のうち）</Heading>
            <AreaContent>
              <div className='mt-1 flex flex-col gap-2.5'>
                {data.axis_fill.map((a) => (
                  <div key={a.axis} className='grid grid-cols-[120px_1fr_64px] items-center gap-3'>
                    <span className='text-[12px]'>{a.label}</span>
                    <Bar pct={a.pct} color='#4F46E5' />
                    <span className='text-right font-mono text-[12px] tabular-nums'>{a.pct}%</span>
                  </div>
                ))}
              </div>
            </AreaContent>
          </Area>

          {/* 分布 */}
          <div className='flex flex-col gap-4 lg:flex-row'>
            <Area>
              <Heading>素材 上位</Heading>
              <AreaContent>
                {data.material_top.length === 0 ? (
                  <Text color={TextColor.SECONDARY}>データなし</Text>
                ) : (
                  <div className='mt-1 flex flex-col gap-1.5'>
                    {data.material_top.map((m) => (
                      <div key={m.name} className='flex items-center justify-between text-[12px]'>
                        <span className='max-w-[70%] truncate'>{m.name}</span>
                        <span className='font-mono tabular-nums'>{m.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </AreaContent>
            </Area>
            <Area>
              <Heading>用途・シーン 上位</Heading>
              <AreaContent>
                <div className='mt-1 flex flex-col gap-1.5'>
                  {data.scene_top.map((s) => (
                    <div key={s.name} className='flex items-center justify-between text-[12px]'>
                      <span className='max-w-[70%] truncate'>{s.name}</span>
                      <span className='font-mono tabular-nums'>{s.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </AreaContent>
            </Area>
          </div>

          {/* コスト */}
          <Area>
            <Heading>処理コスト（トークン記録より）</Heading>
            <AreaContent>
              <div className='flex flex-wrap gap-6 text-[13px]'>
                <div>
                  <span className='text-tertiary'>入力トークン：</span>
                  <span className='font-mono'>{data.prompt_tokens.toLocaleString()}</span>
                </div>
                <div>
                  <span className='text-tertiary'>出力トークン：</span>
                  <span className='font-mono'>{data.completion_tokens.toLocaleString()}</span>
                </div>
                <div>
                  <span className='text-tertiary'>概算コスト：</span>
                  <span className='font-mono'>${data.est_cost_usd.toFixed(2)}</span>
                  <span className='text-tertiary'>（上限値・キャッシュ未考慮）</span>
                </div>
              </div>
            </AreaContent>
          </Area>
        </div>
      )}
    </>
  )
}

export default ProductUnderstandingCoverageSection
